
import { supabase } from '@/integrations/supabase/client';
import { Team, TeamPlayer } from '@/types/database';

export const teamService = {
  // Team photo management
  async addTeamPhoto(teamId: number, photoUrl: string, caption?: string): Promise<void> {
    // Get user ID - prioritize internal user, then Supabase user
    let userId: string | null = null;
    
    console.log('Adding photo via URL for team:', teamId);
    console.log('All localStorage keys:', Object.keys(localStorage));
    
    // First check internal_user_session which is where useInternalAuth stores the user
    try {
      const sessionData = localStorage.getItem('internal_user_session');
      console.log('internal_user_session data:', sessionData);
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        if (parsed && parsed.user && parsed.user.id) {
          userId = parsed.user.id;
          console.log('Using internal user ID from session:', userId);
        }
      }
    } catch (e) {
      console.log('Error parsing internal_user_session:', e);
    }
    
    // If no internal user found, try Supabase auth
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      userId = user?.id || null;
      if (userId) {
        console.log('Using Supabase user ID for photo upload:', userId);
      } else {
        console.log('No Supabase user found');
      }
    }
    
    if (!userId) {
      console.error('Authentication failed - no user ID found');
      throw new Error('User not authenticated - please log in again');
    }

    const { error } = await supabase
      .from('team_photos')
      .insert({
        team_id: teamId,
        photo_url: photoUrl,
        photo_caption: caption,
        uploaded_by: userId
      });

    if (error) throw error;

    // If this is the first photo and team doesn't have a logo, set it as team logo
    const { data: team } = await supabase
      .from('teams')
      .select('team_logo')
      .eq('team_id', teamId)
      .single();

    if (team && (!team.team_logo || team.team_logo === '')) {
      await supabase
        .from('teams')
        .update({ team_logo: photoUrl, updated_at: new Date().toISOString() })
        .eq('team_id', teamId);
    }
  },

  async getTeamPhotos(teamId: number) {
    const { data, error } = await supabase
      .from('team_photos')
      .select('*')
      .eq('team_id', teamId)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async removeTeamPhoto(photoId: string): Promise<void> {
    const { error } = await supabase
      .from('team_photos')
      .delete()
      .eq('id', photoId);

    if (error) throw error;
  },

  // Team video management
  async addTeamVideo(teamId: number, videoUrl: string, title?: string, description?: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    const internalUserId = localStorage.getItem('internal_user_id');
    
    if (!user && !internalUserId) throw new Error('User not authenticated');
    
    const userId = user?.id || internalUserId;

    const { error } = await supabase
      .from('team_videos')
      .insert({
        team_id: teamId,
        video_url: videoUrl,
        video_title: title,
        video_description: description,
        uploaded_by: userId
      });

    if (error) throw error;
  },

  async getTeamVideos(teamId: number) {
    const { data, error } = await supabase
      .from('team_videos')
      .select('*')
      .eq('team_id', teamId)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async removeTeamVideo(videoId: string): Promise<void> {
    const { error } = await supabase
      .from('team_videos')
      .delete()
      .eq('id', videoId);

    if (error) throw error;
  },
  // Get all teams (with RLS filtering)
  async getTeams(): Promise<Team[]> {
    console.log('Fetching teams...');
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .order('team_name');
    
    if (error) {
      console.error('Error fetching teams:', error);
      throw error;
    }
    console.log('Teams fetched successfully:', data);
    return data || [];
  },

  // Get team by ID
  async getTeamById(teamId: number): Promise<Team | null> {
    console.log('Fetching team by ID:', teamId);
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('team_id', teamId)
      .single();
    
    if (error) {
      console.error('Error fetching team by ID:', error);
      throw error;
    }
    console.log('Team fetched by ID:', data);
    return data;
  },

  // Create a new team - updated to handle both user types
  async createTeam(team: Omit<Team, 'team_id' | 'created_at' | 'updated_at'>): Promise<Team> {
    console.log('Creating team with data:', team);
    
    // Validate required fields
    if (!team.team_name?.trim()) {
      throw new Error('Team name is required');
    }
    
    if (!team.created_by) {
      throw new Error('User ID is required to create a team');
    }

    const { data, error } = await supabase
      .from('teams')
      .insert({
        team_name: team.team_name,
        team_description: team.team_description || null,
        sport_type: team.sport_type || null,
        age_group: team.age_group || null,
        division: team.division || null,
        continent: team.continent || null,
        state_province: team.state_province || null,
        city: team.city || null,
        location: team.location || null,
        team_logo: team.team_logo || null,
        created_by: team.created_by
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error creating team:', error);
      throw error;
    }
    
    console.log('Team created successfully:', data);
    return data;
  },

  // Update team
  async updateTeam(teamId: number, updates: Partial<Team>): Promise<Team> {
    console.log('Updating team:', teamId, updates);
    const { data, error } = await supabase
      .from('teams')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('team_id', teamId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating team:', error);
      throw error;
    }
    console.log('Team updated successfully:', data);
    return data;
  },

  // Get team players - with both Supabase and internal user data
  async getTeamPlayers(teamId: number): Promise<TeamPlayer[]> {
    console.log('Fetching team players for team:', teamId);
    
    try {
      // First get the team players
      const { data: teamPlayers, error: playersError } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', teamId);
      
      if (playersError) {
        console.error('Error fetching team players:', playersError);
        throw playersError;
      }

      if (!teamPlayers || teamPlayers.length === 0) {
        console.log('No team players found for team:', teamId);
        return [];
      }

      // Separate player IDs by type and fetch profiles
      const playerIds = teamPlayers.map(p => p.user_id);
      
      // Fetch Supabase user profiles
      const { data: supabaseProfiles, error: supabaseError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', playerIds);

      // Fetch internal user profiles
      const { data: internalProfiles, error: internalError } = await supabase
        .from('internal_users')
        .select('*')
        .in('id', playerIds);

      if (supabaseError) {
        console.warn('Error fetching Supabase profiles:', supabaseError);
      }
      if (internalError) {
        console.warn('Error fetching internal profiles:', internalError);
      }

      // Combine the data with proper profile information
      const enrichedPlayers = teamPlayers.map(player => {
        // First check if it's a Supabase user
        const supabaseProfile = supabaseProfiles?.find(p => p.id === player.user_id);
        if (supabaseProfile) {
          return {
            ...player,
            user_profile: supabaseProfile,
            user_type: 'supabase'
          };
        }

        // Then check if it's an internal user
        const internalProfile = internalProfiles?.find(p => p.id === player.user_id);
        if (internalProfile) {
          return {
            ...player,
            user_profile: internalProfile,
            user_type: 'internal'
          };
        }

        // Fallback if no profile found
        return {
          ...player,
          user_profile: null,
          user_type: 'unknown'
        };
      });

      console.log('Team players fetched successfully:', enrichedPlayers);
      return enrichedPlayers;
    } catch (error) {
      console.error('Error in getTeamPlayers:', error);
      // Return basic team players data without profiles if there's an error
      const { data: basicPlayers } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', teamId);
      
      return basicPlayers || [];
    }
  },

  // Add player to team
  async addPlayerToTeam(teamId: number, userId: string, position?: string): Promise<TeamPlayer> {
    console.log('Adding player to team:', { teamId, userId, position });
    const { data, error } = await supabase
      .from('team_players')
      .insert({
        team_id: teamId,
        user_id: userId,
        position
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error adding player to team:', error);
      throw error;
    }
    console.log('Player added to team successfully:', data);
    
    // Track activity (fire-and-forget, don't block main action)
    this.trackTeamJoinedActivity(userId, teamId, position).catch(err => 
      console.error('Failed to track team joined activity:', err)
    );
    
    return data;
  },
  
  // Helper to track team joined activity
  async trackTeamJoinedActivity(userId: string, teamId: number, position?: string): Promise<void> {
    try {
      const { activityTrackingService } = await import('./activityTrackingService');
      const team = await this.getTeamById(teamId);
      if (team) {
        await activityTrackingService.recordTeamJoined(
          userId, 
          teamId, 
          team.team_name, 
          position,
          team.sport_id
        );
      }
    } catch (err) {
      console.error('Error tracking team joined:', err);
    }
  },

  // Remove player from team
  async removePlayerFromTeam(teamId: number, userId: string): Promise<void> {
    console.log('Removing player from team:', { teamId, userId });
    
    // Get team name before deletion for activity tracking
    let teamName: string | undefined;
    try {
      const team = await this.getTeamById(teamId);
      teamName = team?.team_name;
    } catch (err) {
      console.error('Error getting team for activity tracking:', err);
    }
    
    const { error } = await supabase
      .from('team_players')
      .delete()
      .eq('team_id', teamId)
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error removing player from team:', error);
      throw error;
    }
    console.log('Player removed from team successfully');
    
    // Track activity (fire-and-forget)
    if (teamName) {
      import('./activityTrackingService').then(({ activityTrackingService }) => {
        activityTrackingService.recordTeamLeft(userId, teamId, teamName!).catch(err =>
          console.error('Failed to track team left activity:', err)
        );
      });
    }
  },

  // Update player position
  async updatePlayerPosition(teamId: number, userId: string, position: string): Promise<void> {
    console.log('Updating player position:', { teamId, userId, position });
    const { error } = await supabase
      .from('team_players')
      .update({ position })
      .eq('team_id', teamId)
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error updating player position:', error);
      throw error;
    }
    console.log('Player position updated successfully');
  },

  // Update coach role
  async updateCoachRole(teamId: number, coachId: string, role: string): Promise<void> {
    console.log('Updating coach role:', { teamId, coachId, role });
    const { error } = await supabase
      .from('team_coaches')
      .update({ role })
      .eq('team_id', teamId)
      .eq('coach_id', coachId);
    
    if (error) {
      console.error('Error updating coach role:', error);
      throw error;
    }
    console.log('Coach role updated successfully');
  },

  // Update support staff role
  async updateSupportStaffRole(teamId: number, staffId: string, role: string): Promise<void> {
    console.log('Updating support staff role:', { teamId, staffId, role });
    const { error } = await supabase
      .from('team_support_staff')
      .update({ role })
      .eq('team_id', teamId)
      .eq('staff_id', staffId);
    
    if (error) {
      console.error('Error updating support staff role:', error);
      throw error;
    }
    console.log('Support staff role updated successfully');
  },

  async getTeamBySlug(slug: string): Promise<any> {
    console.log('Getting team by slug:', slug);
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) {
      console.error('Error getting team by slug:', error);
      throw error;
    }
    
    console.log('Team found:', data);
    return data;
  },

  // Get teams for a user - updated to handle both internal and Supabase users
  async getUserTeams(userId: string): Promise<Team[]> {
    console.log('Fetching user teams for user:', userId);
    const allTeams: Team[] = [];

    try {
      // 1. Get teams where user is the creator/owner
      console.log('Checking teams created by user...');
      const { data: ownedTeams, error: ownedError } = await supabase
        .from('teams')
        .select('*')
        .eq('created_by', userId);

      if (ownedError) {
        console.error('Error fetching owned teams:', ownedError);
      } else if (ownedTeams && ownedTeams.length > 0) {
        console.log('Owned teams found:', ownedTeams);
        allTeams.push(...ownedTeams);
      } else {
        console.log('No owned teams found for user');
      }

      // 2. Get teams where user is a coach
      console.log('Checking teams where user is a coach...');
      const { data: coachedTeams, error: coachError } = await supabase
        .from('team_coaches')
        .select('team:teams(*)')
        .eq('coach_id', userId);

      if (coachError) {
        console.error('Error fetching coached teams:', coachError);
      } else if (coachedTeams && coachedTeams.length > 0) {
        console.log('Coached teams found:', coachedTeams);
        const teams = coachedTeams.map(item => item.team).filter(Boolean);
        allTeams.push(...teams);
      } else {
        console.log('No coached teams found for user');
      }

      // 3. Get teams where user is a player
      console.log('Checking teams where user is a player...');
      const { data: playerTeams, error: playerError } = await supabase
        .from('team_players')
        .select('team:teams(*)')
        .eq('user_id', userId);

      if (playerError) {
        console.error('Error fetching player teams:', playerError);
      } else if (playerTeams && playerTeams.length > 0) {
        console.log('Player teams found:', playerTeams);
        const teams = playerTeams.map(item => item.team).filter(Boolean);
        allTeams.push(...teams);
      } else {
        console.log('No player teams found for user');
      }

      // Remove duplicates based on team_id
      const uniqueTeams = allTeams.filter((team, index, self) => 
        index === self.findIndex(t => t.team_id === team.team_id)
      );

      console.log('All unique user teams:', uniqueTeams);
      return uniqueTeams;
    } catch (error) {
      console.error('Error in getUserTeams:', error);
      return [];
    }
  },

  // Add coach to team
  async addCoachToTeam(teamId: number, coachId: string, role: string = 'assistant_coach'): Promise<void> {
    console.log('Adding coach to team:', { teamId, coachId, role });
    
    // Get current user ID from either Supabase auth or internal storage
    const { data: { user } } = await supabase.auth.getUser();
    const internalUserId = localStorage.getItem('internal_user_id');
    const currentUserId = user?.id || internalUserId;
    
    const { error } = await supabase
      .from('team_coaches')
      .insert({
        team_id: teamId,
        coach_id: coachId,
        role: role,
        added_by: currentUserId
      });

    if (error) {
      console.error('Error adding coach to team:', error);
      throw error;
    }
    console.log('Coach added to team successfully');
  },

  // Remove coach from team
  async removeCoachFromTeam(teamId: number, coachId: string): Promise<void> {
    console.log('Removing coach from team:', { teamId, coachId });
    const { error } = await supabase
      .from('team_coaches')
      .delete()
      .eq('team_id', teamId)
      .eq('coach_id', coachId);

    if (error) {
      console.error('Error removing coach from team:', error);
      throw error;
    }
    console.log('Coach removed from team successfully');
  },

  // Search users across both Supabase and internal users
  async searchUsers(searchTerm: string): Promise<any[]> {
    try {
      // Search Supabase users
      const { data: supabaseUsers, error: supabaseError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Search internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_user_profiles')
        .select('user_id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Combine results
      const combined = [
        ...(supabaseUsers || []).map(user => ({
          id: user.id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'supabase' as const
        })),
        ...(internalUsers || []).map(user => ({
          id: user.user_id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'internal' as const
        }))
      ];

      return combined;
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  },

  // Get team coaches
  async getTeamCoaches(teamId: number) {
    console.log('Fetching team coaches for team:', teamId);
    const { data, error } = await supabase
      .from('team_coaches')
      .select(`
        *,
        coach:coach_id(*)
      `)
      .eq('team_id', teamId);

    if (error) {
      console.error('Error fetching team coaches:', error);
      throw error;
    }
    console.log('Team coaches fetched:', data);
    return data || [];
  },

  // Support Staff Management
  async addSupportStaffToTeam(teamId: number, staffId: string, role: string = 'support', position?: string): Promise<void> {
    try {
      console.log('Adding support staff to team:', { teamId, staffId, role, position });
      
      // Get current user ID from either Supabase auth or internal storage
      const { data: { user } } = await supabase.auth.getUser();
      const internalUserId = localStorage.getItem('internal_user_id');
      const currentUserId = user?.id || internalUserId;
      
      const { error } = await supabase
        .from('team_support_staff')
        .insert({
          team_id: teamId,
          staff_id: staffId,
          role: role,
          position: position || null,
          added_by: currentUserId
        });

      if (error) {
        console.error('Error adding support staff to team:', error);
        throw error;
      }

      console.log('Support staff added to team successfully');
    } catch (error) {
      console.error('Error in addSupportStaffToTeam:', error);
      throw error;
    }
  },

  async getTeamSupportStaff(teamId: number) {
    try {
      console.log('Fetching support staff for team:', teamId);
      
      const { data, error } = await supabase
        .from('team_support_staff')
        .select(`
          *,
          staff:profiles!staff_id(*)
        `)
        .eq('team_id', teamId)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching team support staff:', error);
        throw error;
      }

      console.log('Team support staff fetched successfully:', data);
      return data || [];
    } catch (error) {
      console.error('Error in getTeamSupportStaff:', error);
      throw error;
    }
  },

  async removeSupportStaffFromTeam(teamId: number, staffId: string): Promise<void> {
    try {
      console.log('Removing support staff from team:', { teamId, staffId });
      
      const { error } = await supabase
        .from('team_support_staff')
        .update({ is_active: false })
        .eq('team_id', teamId)
        .eq('staff_id', staffId);

      if (error) {
        console.error('Error removing support staff from team:', error);
        throw error;
      }

      console.log('Support staff removed from team successfully');
    } catch (error) {
      console.error('Error in removeSupportStaffFromTeam:', error);
      throw error;
    }
  }
};
